import * as React from "react";
import styled from "./styled-components";
import {
  DraftHandleValue,
  Editor,
  EditorState,
  getDefaultKeyBinding,
  CompositeDecorator,
  ContentBlock,
  ContentState
} from "draft-js";
import { Grammar, Parser } from "nearley";

// @ts-ignore
import partialGrammar from "./grammar-partial.ne";
// @ts-ignore
import grammar from "./grammar.ne";

export const Word = styled.span`
  font-size: 1em;
  background-color: #eaeaea;
  padding: 5px 5px 5px 5px;
  border-radius: 4px;
  vertical-align: middle;
  color: #6b6b6b;
  transition: 0.5s;
  :hover {
    transition: 0.5s;
    background-color: #e0e0e0;
  }
`;
export const ErrorWord = styled.span`
  font-size: 1em;
  background-color: #ffc8c8;
  padding: 5px 5px 5px 5px;
  border-radius: 4px;
  vertical-align: middle;
  color: #6b6b6b;
  transition: 0.5s;
  :hover {
    transition: 0.5s;
    background-color: #ffafaf;
  }
`;
export const Operator = styled.span`
  padding: 5px 5px 5px 5px;
  color: #4a90e2;
  line-height: 0.9em;
  vertical-align: middle;
  font-size: 1.4em;
`;

const Invalid = styled.span`
  background-color: #ff6161 !important;
  vertical-align: middle;
  font-size: 1em;
`;

const ErrorText = styled.div<any>`
  height: 15px;
  font-size: 12px;
  color: #ff6161;
  margin-bottom: 3px;
  visibility: ${({ error }) => (error ? "visible" : "hidden")};
`;

const Section = styled.div<any>`
  border: 1px solid #d9d9d9;
  border-radius: 4px 4px 0px 0px;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  transition: 0.3s;
  border-color: ${({ selected, valid }) =>
    valid ? (selected ? "#40a9ff" : "#d9d9d9") : "#FF6161 !important"};
  box-shadow: ${({ selected, valid }) =>
    selected
      ? valid
        ? "0 0 0 2px rgba(24, 144, 255, 0.2)"
        : "0 0 0 2px rgba(255, 97, 97, 0.2)"
      : ""};
  :hover {
    border-color: #40a9ff;
    transition: 0.3s;
  }
`;

const EditorContainer = styled.div`
  flex-grow: 1;
  padding-top: 10px;
  padding-bottom: 10px;
  padding-left: 1em;
  padding-right: 1em;
  cursor: text;
  word-wrap: break-word;
`;

const SpinnerContainer = styled.div<any>`
  align-self: center;
  background-clip: border-box;
  padding: 10px 15px;
  line-height: 25px;
  height: 100%;
  text-align: center;
  background-color: ${({ loading }) => (loading ? "#40a9ff" : "#fafafa")};
  border-left: 0.5px solid #d9d9d9;
  cursor: pointer;
  vertical-align: middle;

  user-select: none;
  font-size: 1.4em;
  transition: 0.3s;
  color: ${({ loading }) => (loading ? "#FFFFFF" : "#6b6b6b")};
  :hover {
    background-color: ${({ loading }) => (loading ? "#40a9ff" : "#f3f3f3")};
    transition: 0.3s;
  }
`;

interface IState {
  editorState: EditorState;
  focus: boolean;
  decorators: CompositeDecorator;
  error: boolean;
  errorText: string;
  invalidWords: string[];
}
interface IProps {
  loading: boolean;
  error: string;
  errorWords: string[];
  onSubmit(expr: string): void;
}
function flattenDeep([x, ...xs]): any {
  return x !== undefined
    ? [...(Array.isArray(x) ? flattenDeep(x) : [x]), ...flattenDeep(xs)]
    : [];
}
const flatten = (parseResult: any): any => {
  if (!parseResult) {
    return null;
  }
  if (parseResult instanceof Array) {
    return flattenDeep(parseResult).map(flatten);
  }
  if ("left" in parseResult) {
    return flattenDeep([
      flatten(parseResult.left),
      flatten(parseResult.right),
      flatten(parseResult.operator)
    ]);
  }
  return parseResult;
};

interface IToken {
  value: string;
  location: number;
  type: string;
}

const decorator = (component: any, value: string, start: number) => ({
  component,
  strategy: (
    contentBlock: ContentBlock,
    callback: (start: number, end: number) => void,
    contentState: ContentState
  ) => callback(start, start + value.length)
});
class InputBox extends React.Component<IProps, IState> {
  public readonly state = {
    decorators: new CompositeDecorator([]),
    editorState: EditorState.createEmpty(),
    error: false,
    errorText: "",
    focus: false,
    invalidWords: []
  };
  private editorInput: React.RefObject<Editor>;
  constructor(props: any) {
    super(props);
    this.editorInput = React.createRef();
  }
  public componentDidMount() {
    this.focus();
  }
  public focus = () => {
    if (this.editorInput.current !== null) {
      this.editorInput.current.focus();
      this.setState({ focus: true });
    }
  };
  public onBlur = () => {
    this.setState({ focus: false });
  };
  public blur = () => {
    if (this.editorInput.current !== null) {
      this.editorInput.current.blur();
    }
  };
  public handleChange = (editorState: EditorState) => {
    const contents = editorState.getCurrentContent().getPlainText();
    const parser = new Parser(Grammar.fromCompiled(partialGrammar));
    let error = null;
    try {
      parser.feed(contents);
    } catch (err) {
      error = err;
    }
    if (error === null && parser.results.length > 0) {
      let res = flatten(parser.results[0]);
      if (!(res instanceof Array)) {
        res = [res];
      }
      const decorators = new CompositeDecorator(
        res
          .filter((i: any) => i)
          .map(
            ({ type, value, location }: IToken) =>
              type === "operator"
                ? decorator(Operator, value, location)
                : type === "word"
                  ? this.props.errorWords.indexOf(value.toLowerCase()) > -1
                    ? decorator(ErrorWord, value, location)
                    : decorator(Word, value, location)
                  : decorator(Invalid, value, location)
          )
      );
      const s = EditorState.set(editorState, { decorator: decorators });
      this.setState({
        decorators,
        editorState: s,
        error: false,
        errorText: ""
      });
    } else if (error === null) {
      this.setState({ editorState });
    } else {
      this.setState({
        editorState: this.state.editorState
      });
    }
  };
  public submit = () => {
    const contents = this.state.editorState
      .getCurrentContent()
      .getPlainText()
      .toLowerCase();
    const parser = new Parser(Grammar.fromCompiled(grammar));
    let error = null;
    try {
      parser.feed(contents);
    } catch (err) {
      error = err;
    }
    if (error === null && parser.results.length > 0) {
      this.blur();
      this.setState({
        error: false,
        errorText: ""
      });
      this.props.onSubmit(contents);
    } else if (error !== null) {
      this.setState({
        error: true,
        errorText: error.toString()
      });
    } else {
      this.setState({ error: true, errorText: "Error: incomplete expression" });
    }
  };
  public handleKeyCommand = (command: string): DraftHandleValue => {
    if (command === "enter") {
      this.submit();
      return "handled";
    }
    return "not-handled";
  };
  public keyBinding = (e: React.KeyboardEvent<{}>): string | null => {
    if (e.keyCode === 13) {
      return "enter";
    }
    return getDefaultKeyBinding(e);
  };
  public componentDidUpdate({ errorWords }: IProps) {
    if (this.props.errorWords !== errorWords) {
      this.handleChange(this.state.editorState);
    }
  }
  public render() {
    const { loading } = this.props;
    const loadingError = this.props.error;
    const { editorState, focus, error, errorText } = this.state;
    return (
      <div>
        <ErrorText error={error || loadingError !== ""}>
          {errorText + " " + loadingError}
        </ErrorText>
        <Section selected={focus} valid={!error && loadingError === ""}>
          <EditorContainer onClick={this.focus}>
            <Editor
              ref={this.editorInput}
              placeholder="type an expression..."
              onChange={this.handleChange}
              editorState={editorState}
              onFocus={this.focus}
              onBlur={this.onBlur}
              handleKeyCommand={this.handleKeyCommand}
              keyBindingFn={this.keyBinding}
              stripPastedStyles={true}
            />
          </EditorContainer>
          <SpinnerContainer
            role="button"
            onClick={this.submit}
            loading={loading}
          >
            {loading ? (
              "..."
            ) : (
              <div style={{ margin: 0, padding: 0 }}>&#8776;</div>
            )}
          </SpinnerContainer>
        </Section>
      </div>
    );
  }
}

export default InputBox;



// WEBPACK FOOTER //
// ./src/InputBox.tsx